import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  LETTER_TEMPLATE_PREVIEW_SAMPLES,
  letterTemplatePlaceholderLabel,
} from '../adminProspectLetterTemplateDisplay.js';
import { LETTER_TEMPLATE_FONT_CSS } from './LetterFontExtension.js';
import {
  LETTER_BODY_FONT_SIZE_PT,
  LETTER_HEADING_SPACING_AFTER_PT,
  LETTER_HEADING_LINE_HEIGHT,
  LETTER_PAGE_HEIGHT_PT,
  LETTER_PAGE_MARGIN_PT,
  LETTER_PAGE_WIDTH_PT,
  LETTER_PARAGRAPH_LINE_HEIGHT,
  LETTER_PARAGRAPH_SPACING_AFTER_PT,
  LETTER_PREVIEW_MAX_WIDTH_CSS,
  LETTER_PRINTABLE_HEIGHT_PT,
  LETTER_PRINTABLE_WIDTH_PT,
  LETTER_QR_CALLOUT_BACKGROUND_COLOR,
  LETTER_QR_CALLOUT_BORDER_COLOR,
  LETTER_QR_CALLOUT_BORDER_WIDTH_PT,
  LETTER_QR_CALLOUT_GAP_PT,
  LETTER_QR_CALLOUT_PADDING_PT,
  LETTER_QR_CALLOUT_QR_BACKGROUND_COLOR,
  LETTER_QR_CALLOUT_RADIUS_PT,
  LETTER_QR_CALLOUT_SPACING_AFTER_PT,
  LETTER_QR_CALLOUT_SPACING_BEFORE_PT,
  LETTER_QR_CALLOUT_SUBTITLE_COLOR,
  LETTER_QR_CALLOUT_SUBTITLE_EN,
  LETTER_QR_CALLOUT_SUBTITLE_FR,
  LETTER_QR_CALLOUT_TITLE_COLOR,
  LETTER_QR_CALLOUT_TITLE_EN,
  LETTER_QR_CALLOUT_TITLE_FR,
  LETTER_QR_PLACEHOLDER_LABEL,
  LETTER_QR_PREVIEW_WIDTH_PT,
  LETTER_QR_QUIET_ZONE_PT,
  computeLetterTemplatePreviewScale,
  computeLetterTemplateQrCalloutLayout,
  letterLogicalCssPx,
} from './letterTemplatePageConstants.js';
import {
  filterQrBlocksFromLetterTemplateDocument,
  letterTemplatePreviewContainsTokenConcept,
  measureLetterTemplateLogicalOverflow,
  resolveBilingualSharedQrPreview,
  resolveLetterTemplatePreviewFontSizePt,
} from './letterTemplatePreview.js';

function resolvePlaceholderKey(key) {
  if (Object.prototype.hasOwnProperty.call(LETTER_TEMPLATE_PREVIEW_SAMPLES, key)) {
    return LETTER_TEMPLATE_PREVIEW_SAMPLES[key];
  }
  return '';
}

function fontStyleFromMarks(marks) {
  if (!Array.isArray(marks)) return {};
  const style = {};
  const fontMark = marks.find((m) => m.type === 'letterFont');
  const family = fontMark?.attrs?.family || 'helvetica';
  style.fontFamily = LETTER_TEMPLATE_FONT_CSS[family] || LETTER_TEMPLATE_FONT_CSS.helvetica;
  const pt = resolveLetterTemplatePreviewFontSizePt(marks);
  style.fontSize = letterLogicalCssPx(pt);
  return style;
}

function renderMarks(children, marks, context) {
  if (!Array.isArray(marks) || marks.length === 0) {
    if (context?.blockType === 'heading' || context?.blockType === 'paragraph') {
      const pt = resolveLetterTemplatePreviewFontSizePt(marks, context);
      return <span style={{ fontSize: letterLogicalCssPx(pt) }}>{children}</span>;
    }
    return children;
  }
  const style = fontStyleFromMarks(marks);
  if (context?.blockType === 'heading') {
    style.fontSize = letterLogicalCssPx(resolveLetterTemplatePreviewFontSizePt(marks, context));
  }
  let result = Object.keys(style).length ? <span style={style}>{children}</span> : children;
  return marks.reduce((acc, mark) => {
    if (mark.type === 'bold') return <strong>{acc}</strong>;
    if (mark.type === 'italic') return <em>{acc}</em>;
    if (mark.type === 'strike') return <s>{acc}</s>;
    return acc;
  }, result);
}

function renderInlineContent(content, t, context) {
  if (!Array.isArray(content)) return null;
  return content.map((node, index) => {
    if (node.type === 'text') {
      return (
        <React.Fragment key={`text-${index}`}>
          {renderMarks(node.text, node.marks, context)}
        </React.Fragment>
      );
    }
    if (node.type === 'hardBreak') {
      return <br key={`br-${index}`} />;
    }
    if (node.type === 'letterPlaceholder') {
      const resolved = resolvePlaceholderKey(node.attrs?.key);
      const label = resolved || letterTemplatePlaceholderLabel(node.attrs?.key, t);
      return (
        <React.Fragment key={`ph-${index}`}>
          {renderMarks(label, node.marks, context)}
        </React.Fragment>
      );
    }
    return null;
  });
}

function PreviewImage({ previewUrl, alt, t, onLayoutChange, widthPct }) {
  useEffect(() => {
    onLayoutChange?.();
  }, [previewUrl, onLayoutChange, widthPct]);

  if (previewUrl) {
    return (
      <img
        src={previewUrl}
        alt={alt || ''}
        style={{ width: '100%', height: 'auto', display: 'block' }}
        onLoad={onLayoutChange}
        onError={onLayoutChange}
      />
    );
  }
  return (
    <div
      className="flex items-center justify-center border border-dashed border-slate-300 bg-slate-50 text-slate-500"
      style={{ minHeight: letterLogicalCssPx(48), fontSize: letterLogicalCssPx(9) }}
    >
      {t('admin.prospection.letterTemplate.image.previewUnavailable')}
    </div>
  );
}

function renderImageBlock(node, keyPrefix, getAssetPreviewUrl, t, onLayoutChange) {
  const { assetId, widthPct, align, alt } = node.attrs || {};
  const previewUrl = typeof getAssetPreviewUrl === 'function' ? getAssetPreviewUrl(assetId) : null;
  const width = `${widthPct || 50}%`;
  return (
    <div key={keyPrefix} style={{ textAlign: align || 'center', margin: `${letterLogicalCssPx(12)} 0` }}>
      <div style={{ width, maxWidth: '100%', display: 'inline-block' }}>
        <PreviewImage
          previewUrl={previewUrl}
          alt={alt}
          t={t}
          onLayoutChange={onLayoutChange}
          widthPct={widthPct}
        />
      </div>
    </div>
  );
}

function renderQrBlock(node, keyPrefix, { bilingual = false } = {}) {
  const layout = computeLetterTemplateQrCalloutLayout({
    startY: 0,
    align: node?.attrs?.align,
    bilingual,
  });
  const qrAlign = layout.align;
  return (
    <div
      key={keyPrefix}
      style={{
        textAlign: qrAlign,
        margin: `${letterLogicalCssPx(LETTER_QR_CALLOUT_SPACING_BEFORE_PT)} 0 ${letterLogicalCssPx(LETTER_QR_CALLOUT_SPACING_AFTER_PT)}`,
      }}
    >
      <div
        style={{
          display: 'inline-block',
          boxSizing: 'border-box',
          width: letterLogicalCssPx(layout.width),
          height: letterLogicalCssPx(layout.height),
          maxWidth: '100%',
          padding: letterLogicalCssPx(LETTER_QR_CALLOUT_PADDING_PT),
          borderRadius: letterLogicalCssPx(LETTER_QR_CALLOUT_RADIUS_PT),
          border: `${letterLogicalCssPx(LETTER_QR_CALLOUT_BORDER_WIDTH_PT)} solid ${LETTER_QR_CALLOUT_BORDER_COLOR}`,
          background: LETTER_QR_CALLOUT_BACKGROUND_COLOR,
          textAlign: 'left',
        }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: letterLogicalCssPx(LETTER_QR_CALLOUT_GAP_PT),
            height: letterLogicalCssPx(LETTER_QR_PREVIEW_WIDTH_PT),
          }}
        >
          <div
            style={{
              boxSizing: 'border-box',
              width: letterLogicalCssPx(LETTER_QR_PREVIEW_WIDTH_PT),
              height: letterLogicalCssPx(LETTER_QR_PREVIEW_WIDTH_PT),
              flexShrink: 0,
              background: LETTER_QR_CALLOUT_QR_BACKGROUND_COLOR,
              padding: letterLogicalCssPx(LETTER_QR_QUIET_ZONE_PT),
            }}
          >
            <div
              style={{
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: letterLogicalCssPx(8),
                fontWeight: 700,
                letterSpacing: '0.08em',
                color: LETTER_QR_CALLOUT_TITLE_COLOR,
              }}
            >
              {LETTER_QR_PLACEHOLDER_LABEL}
            </div>
          </div>
          <div
            style={{
              minWidth: 0,
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              color: LETTER_QR_CALLOUT_TITLE_COLOR,
            }}
          >
            <p
              style={{
                margin: 0,
                fontSize: letterLogicalCssPx(bilingual ? 9 : 10),
                fontWeight: 700,
                lineHeight: 1.25,
              }}
            >
              {LETTER_QR_CALLOUT_TITLE_FR}
            </p>
            {bilingual ? (
              <p
                style={{
                  margin: `${letterLogicalCssPx(2)} 0 0`,
                  fontSize: letterLogicalCssPx(9),
                  fontWeight: 700,
                  lineHeight: 1.25,
                }}
              >
                {LETTER_QR_CALLOUT_TITLE_EN}
              </p>
            ) : null}
            <p
              style={{
                margin: `${letterLogicalCssPx(4)} 0 0`,
                fontSize: letterLogicalCssPx(bilingual ? 7.5 : 8),
                fontWeight: 500,
                lineHeight: 1.3,
                color: LETTER_QR_CALLOUT_SUBTITLE_COLOR,
              }}
            >
              {bilingual
                ? `${LETTER_QR_CALLOUT_SUBTITLE_FR} · ${LETTER_QR_CALLOUT_SUBTITLE_EN}`
                : LETTER_QR_CALLOUT_SUBTITLE_FR}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function renderBlock(node, t, keyPrefix, getAssetPreviewUrl, onLayoutChange, options = {}) {
  if (!node || typeof node !== 'object') return null;
  const { omitQr = false } = options;
  const align = node.attrs?.align;
  const style = align ? { textAlign: align } : undefined;

  if (node.type === 'paragraph') {
    const context = { blockType: 'paragraph' };
    return (
      <p
        key={keyPrefix}
        style={{
          ...style,
          margin: `0 0 ${letterLogicalCssPx(LETTER_PARAGRAPH_SPACING_AFTER_PT)}`,
          lineHeight: LETTER_PARAGRAPH_LINE_HEIGHT,
          fontSize: letterLogicalCssPx(LETTER_BODY_FONT_SIZE_PT),
        }}
      >
        {renderInlineContent(node.content, t, context)}
      </p>
    );
  }
  if (node.type === 'heading') {
    const level = node.attrs?.level || 1;
    const context = { blockType: 'heading', headingLevel: level };
    const pt = resolveLetterTemplatePreviewFontSizePt(node.content?.[0]?.marks, context);
    const Tag = level === 1 ? 'h1' : level === 2 ? 'h2' : 'h3';
    const marginBottom =
      LETTER_HEADING_SPACING_AFTER_PT[level] || LETTER_HEADING_SPACING_AFTER_PT[3];
    return (
      <Tag
        key={keyPrefix}
        style={{
          ...style,
          margin: `0 0 ${letterLogicalCssPx(marginBottom)}`,
          lineHeight: LETTER_HEADING_LINE_HEIGHT,
          fontSize: letterLogicalCssPx(pt),
          fontWeight: 700,
        }}
      >
        {renderInlineContent(node.content, t, context)}
      </Tag>
    );
  }
  if (node.type === 'bulletList') {
    return (
      <ul
        key={keyPrefix}
        style={{ margin: `0 0 ${letterLogicalCssPx(LETTER_PARAGRAPH_SPACING_AFTER_PT)}`, paddingLeft: letterLogicalCssPx(18), fontSize: letterLogicalCssPx(LETTER_BODY_FONT_SIZE_PT) }}
      >
        {(node.content || []).map((item, i) =>
          renderBlock(item, t, `${keyPrefix}-li-${i}`, getAssetPreviewUrl, onLayoutChange, options),
        )}
      </ul>
    );
  }
  if (node.type === 'orderedList') {
    return (
      <ol
        key={keyPrefix}
        style={{ margin: `0 0 ${letterLogicalCssPx(LETTER_PARAGRAPH_SPACING_AFTER_PT)}`, paddingLeft: letterLogicalCssPx(18), fontSize: letterLogicalCssPx(LETTER_BODY_FONT_SIZE_PT) }}
      >
        {(node.content || []).map((item, i) =>
          renderBlock(item, t, `${keyPrefix}-oli-${i}`, getAssetPreviewUrl, onLayoutChange, options),
        )}
      </ol>
    );
  }
  if (node.type === 'listItem') {
    return (
      <li key={keyPrefix}>
        {(node.content || []).map((child, i) =>
          renderBlock(child, t, `${keyPrefix}-lic-${i}`, getAssetPreviewUrl, onLayoutChange, options),
        )}
      </li>
    );
  }
  if (node.type === 'blockquote') {
    return (
      <blockquote
        key={keyPrefix}
        style={{
          margin: `0 0 ${letterLogicalCssPx(9)}`,
          paddingLeft: letterLogicalCssPx(12),
          borderLeft: `${letterLogicalCssPx(3)} solid #cbd5e1`,
          fontStyle: 'italic',
          color: '#334155',
          fontSize: letterLogicalCssPx(LETTER_BODY_FONT_SIZE_PT),
        }}
      >
        {(node.content || []).map((child, i) =>
          renderBlock(child, t, `${keyPrefix}-bq-${i}`, getAssetPreviewUrl, onLayoutChange, options),
        )}
      </blockquote>
    );
  }
  if (node.type === 'horizontalRule') {
    return (
      <hr
        key={keyPrefix}
        style={{ margin: `${letterLogicalCssPx(12)} 0`, border: 'none', borderTop: `${letterLogicalCssPx(1)} solid #cbd5e1` }}
      />
    );
  }
  if (node.type === 'letterQrBlock') {
    if (omitQr) return null;
    return renderQrBlock(node, keyPrefix, { bilingual: false });
  }
  if (node.type === 'letterImage') {
    return renderImageBlock(node, keyPrefix, getAssetPreviewUrl, t, onLayoutChange);
  }
  return null;
}

export function renderLetterTemplatePreviewDocument(
  document,
  t,
  getAssetPreviewUrl,
  onLayoutChange,
  options = {},
) {
  if (!document || document.type !== 'doc' || !Array.isArray(document.content)) {
    return null;
  }
  return document.content.map((node, index) =>
    renderBlock(node, t, `block-${index}`, getAssetPreviewUrl, onLayoutChange, options),
  );
}

function SharedBilingualQrPreview({ qrNode }) {
  return renderQrBlock(qrNode, 'shared-qr', { bilingual: true });
}

export default function LetterTemplatePreview({
  frenchDocument,
  englishDocument,
  bilingual = false,
  letterDocument,
  t,
  getAssetPreviewUrl,
}) {
  const outerRef = useRef(null);
  const naturalContentRef = useRef(null);
  const [scale, setScale] = useState(1);
  const [overflowHint, setOverflowHint] = useState(false);
  const [layoutGeneration, setLayoutGeneration] = useState(0);

  const frDoc = frenchDocument || letterDocument;
  const { sharedQr, omitFromFlow } = resolveBilingualSharedQrPreview(frDoc, englishDocument, bilingual);
  const frPreviewDoc = omitFromFlow ? filterQrBlocksFromLetterTemplateDocument(frDoc) : frDoc;
  const enPreviewDoc =
    bilingual && englishDocument && omitFromFlow
      ? filterQrBlocksFromLetterTemplateDocument(englishDocument)
      : englishDocument;

  const scheduleOverflowMeasure = useCallback(() => {
    setLayoutGeneration((value) => value + 1);
  }, []);

  useEffect(() => {
    const outerEl = outerRef.current;
    const contentEl = naturalContentRef.current;
    if (!outerEl || !contentEl) return undefined;

    const updateScale = () => {
      const availableWidth = outerEl.clientWidth;
      if (availableWidth > 0) {
        setScale(computeLetterTemplatePreviewScale(availableWidth));
      }
    };

    const measureOverflow = () => {
      setOverflowHint(
        measureLetterTemplateLogicalOverflow(contentEl.scrollHeight, LETTER_PRINTABLE_HEIGHT_PT),
      );
    };

    const measure = () => {
      updateScale();
      measureOverflow();
    };

    measure();

    let resizeObserver;
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(measure);
      resizeObserver.observe(outerEl);
      resizeObserver.observe(contentEl);
    }

    window.addEventListener('resize', measure);
    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [frDoc, englishDocument, bilingual, layoutGeneration, getAssetPreviewUrl, omitFromFlow]);

  const scaledHeight = LETTER_PAGE_HEIGHT_PT * scale;

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
          {t('admin.prospection.letterTemplate.preview.title')}
        </p>
        <p className="text-xs text-slate-500">
          {t('admin.prospection.letterTemplate.preview.usLetterScaled')}
        </p>
      </div>
      <div ref={outerRef} className="mx-auto w-full" style={{ maxWidth: LETTER_PREVIEW_MAX_WIDTH_CSS }}>
        <div
          className="relative mx-auto overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm"
          style={{
            width: '100%',
            height: `${scaledHeight}px`,
          }}
        >
          <div
            style={{
              width: letterLogicalCssPx(LETTER_PAGE_WIDTH_PT),
              height: letterLogicalCssPx(LETTER_PAGE_HEIGHT_PT),
              transform: `scale(${scale})`,
              transformOrigin: 'top left',
              position: 'absolute',
              top: 0,
              left: 0,
            }}
          >
            <div
              style={{
                boxSizing: 'border-box',
                width: letterLogicalCssPx(LETTER_PAGE_WIDTH_PT),
                height: letterLogicalCssPx(LETTER_PAGE_HEIGHT_PT),
                padding: letterLogicalCssPx(LETTER_PAGE_MARGIN_PT),
                color: '#0f172a',
              }}
            >
              <div
                style={{
                  width: letterLogicalCssPx(LETTER_PRINTABLE_WIDTH_PT),
                  height: letterLogicalCssPx(LETTER_PRINTABLE_HEIGHT_PT),
                  overflow: 'hidden',
                }}
              >
                <div ref={naturalContentRef}>
                  {renderLetterTemplatePreviewDocument(
                    frPreviewDoc,
                    t,
                    getAssetPreviewUrl,
                    scheduleOverflowMeasure,
                    { omitQr: omitFromFlow },
                  )}
                  {bilingual && enPreviewDoc ? (
                    <div
                      style={{
                        marginTop: letterLogicalCssPx(12),
                        paddingTop: letterLogicalCssPx(9),
                        borderTop: `${letterLogicalCssPx(1)} dashed #e2e8f0`,
                      }}
                    >
                      {renderLetterTemplatePreviewDocument(
                        enPreviewDoc,
                        t,
                        getAssetPreviewUrl,
                        scheduleOverflowMeasure,
                        { omitQr: omitFromFlow },
                      )}
                    </div>
                  ) : null}
                  {omitFromFlow && sharedQr ? (
                    <SharedBilingualQrPreview qrNode={sharedQr} />
                  ) : null}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {overflowHint ? (
        <p className="text-sm text-amber-800" role="status">
          {t('admin.prospection.letterTemplate.preview.overflowHint')}
        </p>
      ) : null}
    </div>
  );
}

export { letterTemplatePreviewContainsTokenConcept };
